"use client";

import React from "react";
import { useLocation } from "react-router-dom";
import { useFunnelNavigate } from "@/hooks/useFunnelNavigate";
import { useTranslation } from "react-i18next";
import { FunnelState, ProductVariant } from "@/hooks/useFunnelState"; // Import FunnelState interface
import { OfferResponse } from "@/types/simpego-api";

interface UseFunnelNavigationProps {
  funnelState: FunnelState;
  getLocalizedPath: (pathKey: string) => string;
  handleResetFunnel: () => void;
  discardCoverageCustomizations: (product: ProductVariant | null, recommendation: OfferResponse | null) => void;
}

export const useFunnelNavigation = ({
  funnelState,
  getLocalizedPath,
  handleResetFunnel,
  discardCoverageCustomizations,
}: UseFunnelNavigationProps) => {
  const navigate = useFunnelNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  const {
    petType,
    petName,
    breed,
    birthDate,
    selectedBreedType,
    gender,
    selectedProduct,
    hasSelectedPlan,
    bundleRecommendations,
    finalPrice,
    contactFirstName, // Use contactFirstName
    contactLastName,  // Use contactLastName
    contactEmail,
    isOfferLoadingFromUrl,
    isHydrated,
  } = funnelState;

  const recommendationForSelectedProduct = React.useMemo((): OfferResponse | null => {
    if (!selectedProduct || !bundleRecommendations) return null;
    return bundleRecommendations[selectedProduct.id.toUpperCase() as "S" | "M" | "L"] ?? null;
  }, [selectedProduct, bundleRecommendations]);

  // Navigation functions
  const goToHome = React.useCallback(() => {
    handleResetFunnel(); // Reset state
    // After resetting, navigate directly to the pet details page as the new starting point.
    navigate(getLocalizedPath('routes.petDetails'), { replace: true, state: { fromReset: true } });
  }, [handleResetFunnel, navigate, getLocalizedPath]);

  const goToPetDetails = React.useCallback(() => {
    // Returning to step 1 discards any step-3 *coverage* customizations (deductibles,
    // sub-toggles, VS) immediately; payment interval/method, daily termination, start of
    // insurance etc. are left as the customer set them.
    discardCoverageCustomizations(selectedProduct, recommendationForSelectedProduct);
    navigate(getLocalizedPath('routes.petDetails'));
  }, [navigate, getLocalizedPath, discardCoverageCustomizations, selectedProduct, recommendationForSelectedProduct]);

  const goToProductVariantSelection = React.useCallback(() => {
    // Returning to the product variant table discards any step-3 coverage customizations so
    // the table reflects the (unmodified) recommendation again.
    discardCoverageCustomizations(selectedProduct, recommendationForSelectedProduct);
    navigate(getLocalizedPath('routes.productVariantSelection'));
  }, [navigate, getLocalizedPath, discardCoverageCustomizations, selectedProduct, recommendationForSelectedProduct]);

  /** `scrollTo: "payment"` makes the customization page open at "Zahlung & Versicherungsbeginn". */
  const goToProductCustomizationOptions = React.useCallback((options?: { scrollTo?: "payment" }) => {
    navigate(getLocalizedPath('routes.productCustomization'), options?.scrollTo ? { state: { scrollTo: options.scrollTo } } : undefined);
  }, [navigate, getLocalizedPath]);

  const goToContactDetails = React.useCallback(() => {
    navigate(getLocalizedPath('routes.addressDetails'));
  }, [navigate, getLocalizedPath]);

  const goToConfirmation = React.useCallback(() => {
    navigate(getLocalizedPath('routes.confirmation'));
  }, [navigate, getLocalizedPath]);

  // Safety net for native browser back/forward navigation, which changes location.pathname
  // without going through goToPetDetails/goToProductVariantSelection above: landing back on
  // step 1 or step 2 from a later step should still discard step-3 coverage customizations.
  const previousPathRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;
    previousPathRef.current = currentPath;
    if (previousPath === null || previousPath === currentPath) return;

    const translatedPetDetailsPath = getLocalizedPath('routes.petDetails');
    const translatedHomePath = getLocalizedPath('routes.home');
    const translatedProductVariantSelectionPath = getLocalizedPath('routes.productVariantSelection');
    const laterStepPaths = [
      getLocalizedPath('routes.productCustomization'),
      getLocalizedPath('routes.addressDetails'),
      getLocalizedPath('routes.confirmation'),
    ];

    const isReturningToStep1Or2 =
      currentPath === translatedPetDetailsPath ||
      currentPath === translatedHomePath ||
      currentPath === translatedProductVariantSelectionPath;

    if (isReturningToStep1Or2 && laterStepPaths.includes(previousPath)) {
      discardCoverageCustomizations(selectedProduct, recommendationForSelectedProduct);
    }
  }, [location.pathname, getLocalizedPath, discardCoverageCustomizations, selectedProduct, recommendationForSelectedProduct]);

  // Effect for handling redirects based on funnel state
  React.useEffect(() => {
    console.log("useFunnelNavigation useEffect running. isOfferLoadingFromUrl:", isOfferLoadingFromUrl);
    console.log("Current path:", location.pathname);
    if (!isHydrated) {
      console.log("Skipping navigation checks until hydration completes.");
      return;
    }
    console.log("Dependencies:", { petType, petName, breed, birthDate, selectedBreedType, gender, hasSelectedPlan, finalPrice, contactFirstName, contactLastName, contactEmail });

    if (isOfferLoadingFromUrl) {
      console.log("Skipping navigation checks because offer is loading from URL.");
      return;
    }

    const currentPath = location.pathname;
    const translatedHomePath = getLocalizedPath('routes.home');
    const translatedPetDetailsPath = getLocalizedPath('routes.petDetails');
    const translatedProductVariantSelectionPath = getLocalizedPath('routes.productVariantSelection');
    const translatedProductCustomizationPath = getLocalizedPath('routes.productCustomization');
    const translatedAddressDetailsPath = getLocalizedPath('routes.addressDetails');
    const translatedConfirmationPath = getLocalizedPath('routes.confirmation');

    const navState = location.state as any;

    // If we just came from a reset and are on the pet details page, do not redirect further.
    if (navState?.fromReset && currentPath === translatedPetDetailsPath) {
      console.log("On pet details page after reset. Skipping further auto-redirects.");
      return;
    }
    // If on pet details and petType is missing, this is the intended starting point after a reset, so do nothing.
    // No explicit redirect needed here for `translatedPetDetailsPath` if `!petType`.

    if (currentPath === translatedConfirmationPath &&
        hasSelectedPlan && finalPrice !== null && contactFirstName && contactLastName && contactEmail && birthDate !== undefined) {
      console.log("Currently on confirmation page with all data, no redirect needed.");
      return;
    }

    else if (currentPath === translatedProductVariantSelectionPath && (!petType || !petName || !breed || birthDate === undefined || selectedBreedType === null || gender === null)) {
      console.log("Redirecting to /pet-details from /product-variant-selection due to missing pet details.");
      navigate(translatedPetDetailsPath);
    } else if (currentPath === translatedProductCustomizationPath && (!hasSelectedPlan)) {
      console.log("Redirecting to /product-variant-selection from /product-customization due to missing plan.");
      navigate(translatedProductVariantSelectionPath);
    } else if (currentPath === translatedAddressDetailsPath && (!hasSelectedPlan || finalPrice === null)) {
      console.log("Redirecting to /product-customization from /address-details due to missing plan/price.");
      navigate(translatedProductCustomizationPath);
    } else if (currentPath === translatedConfirmationPath && (!hasSelectedPlan || finalPrice === null || !contactFirstName || !contactLastName || !contactEmail || birthDate === undefined)) {
      console.log("Redirecting to /address-details from /confirmation due to missing confirmation details.");
      navigate(translatedAddressDetailsPath);
    }
  }, [
    location.pathname, navigate, petType, petName, breed, birthDate, selectedBreedType, gender,
    hasSelectedPlan, finalPrice, contactFirstName, contactLastName, contactEmail, isOfferLoadingFromUrl, getLocalizedPath, i18n, isHydrated
  ]);

  return {
    goToHome,
    goToPetDetails,
    goToProductVariantSelection,
    goToProductCustomizationOptions,
    goToContactDetails,
    goToConfirmation,
  };
};
